// Shared date/timezone helpers for availability slots and study sessions.
//
// Availability is a *weekly recurring* pattern, so it is stored as local
// wall-clock slots ({ day, start, end }) plus the IANA zone they were entered
// in (availabilityLocal / availabilityTz on the user doc). The UTC form used
// for cross-timezone matching is derived on the fly by normalizeAvailability(),
// never persisted — that way a DST transition can never leave a stored value
// an hour stale.

import { DateTime } from 'luxon';
import { DAY_SHORTS, DAY_INDEX } from './constants';

const WEEK_MIN = 7 * 24 * 60; // 10080

// ---- timezone plumbing ----------------------------------------------------

// Bare IANA id from a TIMEZONES entry like "America/New_York (EST/EDT)".
export const ianaOf = (tz) => (tz ? String(tz).split(' ')[0].trim() : '');

// The viewer's own zone, used when a profile has no timezone set.
export const browserZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch (e) { return 'UTC'; }
};

// Accepts a suffixed display value ("Europe/Paris (CET/CEST)") or a bare IANA
// id, and always returns something luxon can resolve.
export const resolveZone = (tz) => {
  const iana = ianaOf(tz);
  if (iana && DateTime.local().setZone(iana).isValid) return iana;
  const bz = browserZone();
  return DateTime.local().setZone(bz).isValid ? bz : 'UTC';
};

// ---- small parsing helpers ---------------------------------------------------

const hhmmToMin = (s) => {
  const [h, m] = String(s).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Legacy availability strings were "<Day> <block>" where <block> was one of a
// fixed set of 12h ranges. Map those exactly; fall back to a loose parse.
const LEGACY_BLOCKS = {
  '8:00-10:00 AM':     { start: '08:00', end: '10:00' },
  '10:00 AM-12:00 PM': { start: '10:00', end: '12:00' },
  '12:00-2:00 PM':     { start: '12:00', end: '14:00' },
  '2:00-4:00 PM':      { start: '14:00', end: '16:00' },
  '4:00-6:00 PM':      { start: '16:00', end: '18:00' },
  '6:00-8:00 PM':      { start: '18:00', end: '20:00' },
  '8:00-10:00 PM':     { start: '20:00', end: '22:00' },
};

// "H:MM" plus an AM/PM flag -> "HH:MM".
const to24 = (raw, isPM) => {
  const [h, m] = raw.replace(/AM|PM/i, '').trim().split(':').map(Number);
  let hr = h % 12;
  if (isPM) hr += 12;
  return `${String(hr).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
};

const parseLegacySlot = (str) => {
  if (!str || typeof str !== 'string') return null;
  const day = str.split(' ')[0];
  if (!(day in DAY_INDEX)) return null;
  const blockStr = str.slice(day.length + 1).trim();

  const known = LEGACY_BLOCKS[blockStr];
  if (known) return { day, start: known.start, end: known.end };

  // Loose fallback for any block string not in the table: "start-end", where an
  // AM/PM marker may sit on either half and carries to a bare half.
  const parts = blockStr.split('-').map((s) => s.trim());
  if (parts.length !== 2) return null;
  const startAM = /AM/i.test(parts[0]);
  const startPM = /PM/i.test(parts[0]);
  const endAM = /AM/i.test(parts[1]);
  const endPM = /PM/i.test(parts[1]);
  const endIsPM = endPM || (!endAM && (startPM || (!startAM && Number(parts[1].split(':')[0]) < 8)));
  const startIsPM = startPM || (!startAM && endIsPM && Number(parts[0].split(':')[0]) !== 12);
  return { day, start: to24(parts[0], startIsPM), end: to24(parts[1], endIsPM) };
};

// ---- local slot  <->  weekly-UTC minutes -----------------------------------

// Next occurrence (at or after `from`) of `dayShort` at wall-clock HH:MM in `iana`.
const nextLocalOccurrence = (dayShort, hhmm, iana, from) => {
  const base = DateTime.fromJSDate(from, { zone: iana });
  const [h, m] = String(hhmm).split(':').map(Number);
  // luxon weekday: 1=Mon..7=Sun; DAY_INDEX is 0=Mon..6=Sun.
  const target = DAY_INDEX[dayShort] + 1;
  let d = base.set({ weekday: target, hour: h, minute: m || 0, second: 0, millisecond: 0 });
  if (d < base) d = d.plus({ weeks: 1 });
  return d;
};

// One local weekly slot -> { startMin, endMin } as minutes from Monday 00:00
// UTC, for the week that contains its next occurrence. DST-correct for that
// week because the offset comes from the real upcoming date.
export const slotToUtcMin = (slot, iana, from = new Date()) => {
  if (!slot || !slot.day || !slot.start || !(slot.day in DAY_INDEX) || !iana) return null;
  const startLocal = nextLocalOccurrence(slot.day, slot.start, iana, from);
  if (!startLocal.isValid) return null;
  const durMin = Math.max(0, hhmmToMin(slot.end) - hhmmToMin(slot.start)) || 120;
  const startUtc = startLocal.toUTC();
  const startMin = ((startUtc.weekday - 1) * 1440 + startUtc.hour * 60 + startUtc.minute + WEEK_MIN) % WEEK_MIN;
  return { startMin, endMin: (startMin + durMin) % WEEK_MIN };
};

// ---- normalization --------------------------------------------------------

// Always returns { tz, local: [{day,start,end}], utc: [{startMin,endMin}] }.
// Prefers the new fields; converts a legacy `availability` string array only
// when availabilityLocal is absent entirely.
export const normalizeAvailability = (userDoc, from = new Date()) => {
  const doc = userDoc || {};
  const tz = doc.availabilityTz && DateTime.local().setZone(doc.availabilityTz).isValid
    ? doc.availabilityTz
    : resolveZone(doc.timezone);

  let local;
  if (Array.isArray(doc.availabilityLocal)) {
    local = doc.availabilityLocal
      .map((s) => (s && s.day in DAY_INDEX && s.start ? { day: s.day, start: s.start, end: s.end || s.start } : null))
      .filter(Boolean);
  } else if (Array.isArray(doc.availability)) {
    local = doc.availability.map(parseLegacySlot).filter(Boolean);
  } else {
    local = [];
  }

  const utc = local.map((s) => slotToUtcMin(s, tz, from)).filter(Boolean);
  return { tz, local, utc };
};

// ---- display -------------------------------------------------------------

// Weekly-UTC minutes -> parts in a viewer's zone, 24-hour.
export const utcMinToLocalParts = (startMin, iana, from = new Date()) => {
  const monday = DateTime.fromJSDate(from, { zone: 'utc' }).startOf('week');
  const inst = monday.plus({ minutes: startMin }).setZone(iana || 'UTC');
  return { dayShort: DAY_SHORTS[inst.weekday - 1], time: inst.toFormat('HH:mm'), weekday: inst.weekday };
};

// "13:00–15:00" in the viewer's zone.
export const utcSlotTimeRange = (slot, iana, from = new Date()) => {
  if (!slot || slot.startMin == null) return '';
  const a = utcMinToLocalParts(slot.startMin, iana, from);
  const b = utcMinToLocalParts(slot.endMin, iana, from);
  return `${a.time}–${b.time}`;
};

// "Mon 13:00–15:00" in the viewer's zone.
export const utcSlotLabel = (slot, iana, from = new Date()) => {
  if (!slot || slot.startMin == null) return '';
  return `${utcMinToLocalParts(slot.startMin, iana, from).dayShort} ${utcSlotTimeRange(slot, iana, from)}`;
};

// ---- concrete session instants -----------------------------------------------

// Legacy: resolve an old "Mon 8:00-10:00 AM" slot string to the next Date in
// the browser's local zone. Kept only for session docs written before the
// timezone rework (they carry `slot` but sometimes no `sessionDate`).
export const slotToDate = (slot, from = new Date()) => {
  if (!slot) return null;
  const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const parts = slot.split(' ');
  const dayN = days[parts[0]];
  if (dayN === undefined) return null;

  const timeRange = parts.slice(1).join(' ');
  const startTime = timeRange.split('-')[0].trim();

  const startHasAM = /AM/i.test(startTime);
  const startHasPM = /PM/i.test(startTime);
  const rangeIsPM = /PM/i.test(timeRange);
  const isPM = (startHasAM || startHasPM) ? startHasPM : rangeIsPM;
  const isAM = (startHasAM || startHasPM) ? startHasAM : !rangeIsPM;

  const timePart = startTime.replace(/AM|PM/gi, '').trim();
  const [hours, minutes] = timePart.split(':').map(Number);
  if (isNaN(hours)) return null;

  let h = hours;
  if (isPM && h !== 12) h += 12;
  if (isAM && h === 12) h = 0;

  const d = new Date(from);
  let diff = dayN - d.getDay();
  if (diff < 0) diff += 7;
  d.setDate(d.getDate() + diff);
  d.setHours(h, minutes || 0, 0, 0);
  if (d <= from) d.setDate(d.getDate() + 7);
  return d;
};

// Next concrete UTC instant for a weekly-UTC slot, strictly in the future.
export const slotUtcToDate = (slot, from = new Date()) => {
  if (!slot || slot.startMin == null) return null;
  const monday = DateTime.fromJSDate(from, { zone: 'utc' }).startOf('week');
  let inst = monday.plus({ minutes: slot.startMin });
  if (inst.toMillis() <= from.getTime()) inst = inst.plus({ weeks: 1 });
  return inst.toJSDate();
};

// Start instant of a session: stored instant if present, else legacy slot.
export const sessionStart = (session) => {
  if (session && session.sessionDate) {
    const d = new Date(session.sessionDate);
    if (!isNaN(d.getTime())) return d;
  }
  if (session && session.startMin != null) return slotUtcToDate({ startMin: session.startMin });
  return slotToDate(session && session.slot);
};

// "Mon, Sep 1" in a viewer's zone.
export const dateLabel = (d, iana) =>
  d ? DateTime.fromJSDate(d, { zone: iana || undefined }).toFormat('ccc, LLL d') : '';

// "Mon, Sep 1 · 13:00–15:00" in a viewer's zone, 24-hour.
export const sessionLabel = (session, iana) => {
  const d = sessionStart(session);
  if (!d) return (session && session.slot) || '';
  const start = DateTime.fromJSDate(d, { zone: iana || undefined });
  const durMin = (session && session.startMin != null && session.endMin != null)
    ? (((session.endMin - session.startMin) % WEEK_MIN) + WEEK_MIN) % WEEK_MIN || 120
    : 120;
  const end = start.plus({ minutes: durMin });
  return `${start.toFormat('ccc, LLL d')} · ${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')}`;
};

// "New York" from "America/New_York", for one-line timezone notes.
export const zoneCity = (zone) => (ianaOf(zone) || 'your timezone').split('/').pop().replace(/_/g, ' ');
